import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import axios from 'axios';
import {
  ArrowLeft, Loader2, Check, XCircle, Clock, FileAudio,
  ExternalLink, Download, Music, Sparkles,
} from 'lucide-react';
import { authSelector } from '../../features/auth/slice';

const PUBLIC_BASE = (() => {
  const h = window.location.hostname;
  return h === 'impink.co.kr' || h === 'www.impink.co.kr' ||
    h === 'urpink.co.kr' || h === 'www.urpink.co.kr' ||
    h === 'localhost' || h === '100.68.115.13' || h === '100.116.73.97' ? '' : '/public';
})();

const ALBUM_TYPE_MAP = { single: '싱글', ep: 'EP', regular: '정규', other: '기타' };
const BEAT_MAP = { purchased: '구매', not_purchased: '구매하지 않음' };
const STATUS_MAP = {
  pending: { label: '검토중', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30', icon: Clock },
  approved: { label: '발매 승인', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', icon: Check },
  rejected: { label: '거절', color: 'text-rose-400 bg-rose-500/10 border-rose-500/30', icon: XCircle },
};

function formatDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function InfoRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b border-white/5 last:border-0">
      <span className="text-slate-500 text-sm font-medium sm:w-44 shrink-0">{label}</span>
      <div className="text-white text-sm flex-1 break-all">{children}</div>
    </div>
  );
}

function PublicProposalViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdminUser = me?.userType === 'client' && me?.adminYn === 'Y';

  useEffect(() => {
    if (!me) {
      navigate(`${PUBLIC_BASE}/login`);
      return;
    }
    setLoading(true);
    axios.get(`/api/distribution-proposal/${id}`)
      .then(({ data }) => setProposal(data))
      .catch((err) => setError(err.response?.data?.errorMessage || '제안을 불러올 수 없습니다.'))
      .finally(() => setLoading(false));
  }, [id, me, navigate]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[400px]"><Loader2 size={32} className="animate-spin text-slate-500" /></div>
      </PublicLayout>
    );
  }

  if (error || !proposal) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <p className="text-rose-400 mb-4">{error || '제안을 찾을 수 없습니다.'}</p>
          <button onClick={() => navigate(-1)} className="text-sky-400 text-sm hover:underline">돌아가기</button>
        </div>
      </PublicLayout>
    );
  }

  const s = STATUS_MAP[proposal.status] || STATUS_MAP.pending;
  const StatusIcon = s.icon;

  return (
    <PublicLayout>
      <SEO title={`유통제안 - ${proposal.artistName}`} path={`/proposal/${id}`} />

      {/* 히어로 */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-violet-600/10 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-8 sm:pt-20 sm:pb-10">
          <button onClick={() => navigate(`${PUBLIC_BASE}/proposal`)} className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> 목록으로
          </button>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white">{proposal.artistName}</h1>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${s.color}`}>
              <StatusIcon size={14} /> {s.label}
            </span>
          </div>
          <p className="text-slate-500 text-sm mt-2">접수일: {formatDate(proposal.createdAt)}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 space-y-8">
        {/* 상태 결과 카드 */}
        {proposal.status !== 'pending' && (
          <div className={`rounded-2xl border p-6 ${proposal.status === 'approved' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-rose-500/5 border-rose-500/20'}`}>
            <div className="flex items-center gap-3 mb-3">
              <StatusIcon size={24} className={proposal.status === 'approved' ? 'text-emerald-400' : 'text-rose-400'} />
              <h3 className={`font-bold text-lg ${proposal.status === 'approved' ? 'text-emerald-400' : 'text-rose-400'}`}>
                {proposal.status === 'approved' ? '발매가 승인되었습니다' : '제안이 거절되었습니다'}
              </h3>
            </div>
            {proposal.status === 'rejected' && proposal.rejectReason && (
              <p className="text-slate-300 text-sm leading-relaxed pl-9">{proposal.rejectReason}</p>
            )}
            {proposal.processedAt && (
              <p className="text-slate-600 text-xs mt-3 pl-9">처리일: {formatDate(proposal.processedAt)}</p>
            )}
          </div>
        )}

        {/* 상세 정보 */}
        <div className="rounded-2xl bg-[#131e33] border border-white/10 p-5 sm:p-8">
          <h2 className="text-white font-bold text-lg mb-5 flex items-center gap-2">
            <Sparkles size={18} className="text-violet-400" /> 제안 상세
          </h2>
          <InfoRow label="아티스트명">{proposal.artistName}</InfoRow>
          <InfoRow label="앨범형태">{ALBUM_TYPE_MAP[proposal.albumType] || proposal.albumType}</InfoRow>
          <InfoRow label="데모 음원 진행 상황">{proposal.demoStatus}</InfoRow>
          <InfoRow label="비트 구매 여부">{BEAT_MAP[proposal.beatPurchase] || proposal.beatPurchase}</InfoRow>
          <InfoRow label="장르">{proposal.genre}</InfoRow>
          <InfoRow label="데모음원">
            <a href={proposal.demoFileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-sky-500/10 border border-sky-500/20 rounded-lg text-sky-400 hover:text-sky-300 text-sm transition-colors">
              <FileAudio size={14} /> {proposal.demoFileName} <Download size={12} />
            </a>
          </InfoRow>
          {proposal.referenceFiles && (
            <InfoRow label="기타 참고자료">
              {proposal.referenceFiles.startsWith('http') ? (
                <a href={proposal.referenceFiles} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1">
                  {proposal.referenceFiles} <ExternalLink size={12} />
                </a>
              ) : proposal.referenceFiles}
            </InfoRow>
          )}
          <InfoRow label="원하는 발매 시기">{proposal.releaseTimeline}</InfoRow>
          <InfoRow label="아티스트 및 앨범 소개">
            <p className="whitespace-pre-wrap">{proposal.introduction}</p>
          </InfoRow>
          <InfoRow label="참고 링크">
            {proposal.referenceLinks.startsWith('http') ? (
              <a href={proposal.referenceLinks} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline flex items-center gap-1">
                {proposal.referenceLinks} <ExternalLink size={12} />
              </a>
            ) : proposal.referenceLinks}
          </InfoRow>
          <InfoRow label="휴대폰 번호">{proposal.phone}</InfoRow>
          <InfoRow label="이메일">{proposal.email}</InfoRow>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PublicProposalViewPage;
